import { lazy, Suspense, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { normalizeMarkdownBullets, isExternalLink } from '../utils';

const CodeBlock = lazy(() => import('./CodeBlock'));

// Hoisted: a fresh `[remarkGfm]` array literal on every render makes
// react-markdown treat the plugin pipeline as changed and rebuild it, which
// is pure waste when it is the same plugin every time.
const REMARK_PLUGINS = [remarkGfm];

interface MarkdownRendererProps {
  content: string;
  isExpanded: boolean;
  isDark?: boolean;
  primaryColor?: string;
  apiUrl?: string;
}

/**
 * Universal markdown renderer that handles any LLM response format.
 *
 * Supports: headings, paragraphs, lists (ordered, unordered, task lists),
 * code blocks (with and without language), inline code, tables, blockquotes,
 * links, images, horizontal rules, emphasis, strikethrough, and more.
 *
 * Uses inline styles to override the widget's CSS resets and guarantee
 * proper rendering regardless of host-page stylesheets.
 */
export function MarkdownRenderer({
  content,
  isExpanded,
  isDark = false,
  primaryColor = '#6720ff',
  apiUrl,
}: MarkdownRendererProps) {
  const fontSize = isExpanded ? '18px' : '15px';
  const smallFontSize = isExpanded ? '16px' : '13px';

  // Theme-aware color tokens
  const colors = {
    text: isDark ? '#e5e7eb' : 'inherit',           // gray-200 / inherit
    textMuted: isDark ? '#9ca3af' : '#6b7280',       // gray-400 / gray-500
    heading: isDark ? '#f3f4f6' : 'inherit',         // gray-100 / inherit
    link: isDark ? '#a78bfa' : (primaryColor || '#6720ff'), // brand primary
    border: isDark ? '#4b5563' : '#e5e7eb',          // gray-600 / gray-200
    borderStrong: isDark ? '#6b7280' : '#d1d5db',    // gray-500 / gray-300
    tableBg: isDark ? '#1f2937' : '#f9fafb',         // gray-800 / gray-50
    inlineCodeBg: isDark ? '#374151' : '#f3f4f6',    // gray-700 / gray-100
    inlineCodeColor: isDark ? '#fb923c' : '#e11d48', // orange-400 / rose-600
    blockquoteBorder: isDark ? '#6b7280' : '#d1d5db',
    blockquoteText: isDark ? '#9ca3af' : '#6b7280',
  };

  // Pre-process content to normalize formatting from any LLM.
  // Memoized: this is nine full-string regex passes, and during streaming the
  // component re-renders once per animation frame with the same text whenever
  // the typer reveals no new characters.
  const processedContent = useMemo(() => preprocessLLMContent(content), [content]);

  return (
    <div className="vw-w-full vw-markdown-content" style={{ width: '100%', maxWidth: '100%', whiteSpace: 'normal' }}>
      <ReactMarkdown
        remarkPlugins={REMARK_PLUGINS}
        components={{
          /* ── Headings ─────────────────────────────────────────── */
          h1: ({ ...props }) => (
            <h1
              {...props}
              style={{
                fontSize: isExpanded ? '28px' : '24px',
                fontWeight: 700,
                margin: '16px 0 8px',
                lineHeight: 1.3,
                display: 'block',
                color: colors.heading,
              }}
            />
          ),
          h2: ({ ...props }) => (
            <h2
              {...props}
              style={{
                fontSize: isExpanded ? '24px' : '20px',
                fontWeight: 700,
                margin: '14px 0 6px',
                lineHeight: 1.3,
                display: 'block',
                color: colors.heading,
              }}
            />
          ),
          h3: ({ ...props }) => (
            <h3
              {...props}
              style={{
                fontSize: isExpanded ? '21px' : '18px',
                fontWeight: 600,
                margin: '12px 0 4px',
                lineHeight: 1.4,
                display: 'block',
                color: colors.heading,
              }}
            />
          ),
          h4: ({ ...props }) => (
            <h4
              {...props}
              style={{
                fontSize: isExpanded ? '19px' : '16px',
                fontWeight: 600,
                margin: '10px 0 4px',
                lineHeight: 1.4,
                display: 'block',
                color: colors.heading,
              }}
            />
          ),
          h5: ({ ...props }) => (
            <h5
              {...props}
              style={{
                fontSize,
                fontWeight: 600,
                margin: '8px 0 4px',
                lineHeight: 1.4,
                display: 'block',
                color: colors.heading,
              }}
            />
          ),
          h6: ({ ...props }) => (
            <h6
              {...props}
              style={{
                fontSize,
                fontWeight: 600,
                margin: '8px 0 4px',
                lineHeight: 1.4,
                display: 'block',
                color: colors.textMuted,
              }}
            />
          ),

          /* ── Paragraph ────────────────────────────────────────── */
          p: ({ ...props }) => (
            <p
              {...props}
              style={{
                marginTop: 0,
                marginBottom: '8px',
                fontSize,
                lineHeight: 1.625,
                color: colors.text,
                whiteSpace: 'normal',
              }}
            />
          ),

          /* ── Lists ────────────────────────────────────────────── */
          ul: ({ ...props }) => (
            <ul
              {...props}
              style={{
                marginTop: '4px',
                marginBottom: '8px',
                paddingLeft: '1.5em',
                listStyleType: 'disc',
              }}
            />
          ),
          ol: ({ ...props }) => (
            <ol
              {...props}
              style={{
                marginTop: '4px',
                marginBottom: '8px',
                paddingLeft: '1.5em',
                listStyleType: 'decimal',
              }}
            />
          ),
          li: ({ children, ...props }: any) => {
            // Detect GFM task-list items (checkbox inside li)
            const hasCheckbox =
              Array.isArray(children) &&
              children.some(
                (child: any) =>
                  typeof child === 'object' &&
                  child?.props?.type === 'checkbox',
              );
            return (
              <li
                {...props}
                style={{
                  marginBottom: '4px',
                  fontSize,
                  display: 'list-item',
                  lineHeight: 1.625,
                  color: colors.text,
                  whiteSpace: 'normal',
                  ...(hasCheckbox
                    ? { listStyleType: 'none', marginLeft: '-1.5em' }
                    : {}),
                }}
              >
                {children}
              </li>
            );
          },

          /* ── Links ────────────────────────────────────────────── */
          a: ({ href, children, ...props }: any) => {
            const isSafe =
              typeof href === 'string' &&
              /^(https?:\/\/|mailto:|tel:|\/|#|\.\/|\.\.\/)/i.test(href);
            const safeHref = isSafe ? href : '#';
            const external = isExternalLink(safeHref, undefined, apiUrl);

            return (
              <a
                {...props}
                href={safeHref}
                target={external ? '_blank' : undefined}
                rel={external ? 'noopener noreferrer' : undefined}
                onClick={!isSafe ? (e: any) => e.preventDefault() : undefined}
                style={{
                  color: colors.link,
                  textDecoration: 'underline',
                  textUnderlineOffset: '2px',
                }}
              >
                {children}
              </a>
            );
          },

          /* ── Emphasis / strong / strikethrough ────────────────── */
          strong: ({ ...props }) => (
            <strong {...props} style={{ fontWeight: 600 }} />
          ),
          em: ({ ...props }) => (
            <em {...props} style={{ fontStyle: 'italic' }} />
          ),
          del: ({ ...props }) => (
            <del {...props} style={{ textDecoration: 'line-through' }} />
          ),

          /* ── Code (inline + block) ───────────────────────────── */
          code({ className, children, ...props }: any) {
            const match = /language-(\w+)/.exec(className || '');
            const codeStr = String(children).replace(/\n$/, '');

            // Fenced code block WITH language
            if (match) {
              return (
                <Suspense
                  fallback={
                    <div
                      style={{
                        padding: '16px',
                        backgroundColor: '#282c34',
                        color: '#abb2bf',
                        borderRadius: '8px',
                        fontFamily: 'monospace',
                        fontSize: '0.875em',
                        whiteSpace: 'pre-wrap',
                        overflow: 'auto',
                      }}
                    >
                      {codeStr}
                    </div>
                  }
                >
                  <CodeBlock language={match[1]}>{codeStr}</CodeBlock>
                </Suspense>
              );
            }

            // Fenced code block WITHOUT language (multiline)
            if (codeStr.includes('\n')) {
              return (
                <div
                  style={{
                    backgroundColor: '#282c34',
                    color: '#abb2bf',
                    padding: '16px',
                    borderRadius: '8px',
                    overflow: 'auto',
                    fontFamily:
                      'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
                    fontSize: '0.875em',
                    lineHeight: 1.6,
                    marginBottom: '8px',
                  }}
                >
                  <pre
                    style={{
                      margin: 0,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    <code>{codeStr}</code>
                  </pre>
                </div>
              );
            }

            // Inline code
            return (
              <code
                style={{
                  backgroundColor: colors.inlineCodeBg,
                  color: colors.inlineCodeColor,
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontSize: '0.875em',
                  fontFamily:
                    'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
                  wordBreak: 'break-word',
                }}
                {...props}
              >
                {children}
              </code>
            );
          },

          // Transparent wrapper – the code component handles all block styling
          pre: ({ children }) => (
            <div style={{ margin: '8px 0' }}>{children}</div>
          ),

          /* ── Blockquote ───────────────────────────────────────── */
          blockquote: ({ ...props }) => (
            <blockquote
              {...props}
              style={{
                borderLeft: `4px solid ${colors.blockquoteBorder}`,
                paddingLeft: '16px',
                margin: '8px 0',
                color: colors.blockquoteText,
                fontStyle: 'italic',
              }}
            />
          ),

          /* ── Tables (GFM) ─────────────────────────────────────── */
          table: ({ ...props }) => (
            <div
              style={{
                overflowX: 'auto',
                marginBottom: '12px',
                borderRadius: '8px',
                border: `1px solid ${colors.border}`,
              }}
            >
              <table
                {...props}
                style={{
                  borderCollapse: 'collapse',
                  width: '100%',
                  fontSize: smallFontSize,
                }}
              />
            </div>
          ),
          thead: ({ ...props }) => (
            <thead {...props} style={{ backgroundColor: colors.tableBg }} />
          ),
          tbody: ({ ...props }) => <tbody {...props} />,
          tr: ({ ...props }) => (
            <tr {...props} style={{ borderBottom: `1px solid ${colors.border}` }} />
          ),
          th: ({ ...props }) => (
            <th
              {...props}
              style={{
                padding: '10px 14px',
                textAlign: 'left',
                fontWeight: 600,
                fontSize: smallFontSize,
                borderBottom: `2px solid ${colors.borderStrong}`,
                whiteSpace: 'nowrap',
                color: colors.heading,
              }}
            />
          ),
          td: ({ ...props }) => (
            <td
              {...props}
              style={{
                padding: '10px 14px',
                textAlign: 'left',
                fontSize: smallFontSize,
                verticalAlign: 'top',
                color: colors.text,
              }}
            />
          ),

          /* ── Horizontal rule ──────────────────────────────────── */
          hr: ({ ...props }) => (
            <hr
              {...props}
              style={{
                border: 'none',
                borderTop: `1px solid ${colors.border}`,
                margin: '16px 0',
              }}
            />
          ),

          /* ── Images ───────────────────────────────────────────── */
          img: ({ ...props }) => (
            <img
              {...props}
              style={{
                maxWidth: '100%',
                height: 'auto',
                borderRadius: '8px',
                margin: '8px 0',
              }}
              loading="lazy"
            />
          ),

          /* ── Task-list checkboxes (GFM) ───────────────────────── */
          input: ({ ...props }: any) => (
            <input
              {...props}
              disabled
              style={{
                marginRight: '8px',
                verticalAlign: 'middle',
                width: '16px',
                height: '16px',
                accentColor: primaryColor || '#6720ff',
              }}
            />
          ),
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * Pre-processor: normalises quirks from any LLM (OpenAI, Claude,
 * Gemini, Llama, Mistral, custom fine-tunes, etc.)
 * ──────────────────────────────────────────────────────────────────── */
export function preprocessLLMContent(content: string): string {
  if (!content) return content;

  let processed = content;

  // 1. Normalise bullet characters (•, ◦, ▪) and fix spacing
  processed = normalizeMarkdownBullets(processed);

  // 2. Ensure blank line before headings (required for markdown parsing)
  //    "some text\n## Heading" → "some text\n\n## Heading"
  processed = processed.replace(/([^\n])\n(#{1,6}\s)/g, '$1\n\n$2');

  // 3. Ensure blank line before fenced code blocks
  processed = processed.replace(/([^\n])\n(```)/g, '$1\n\n$2');

  // 4. Fix numbered lists missing space after period: "1.Item" → "1. Item" (exclude decimals like 1.5)
  processed = processed.replace(/^(\d+\.)(?=[^\s\d])/gm, '$1 ');

  // 5. Fix bullet lists missing space after marker: "-Item" → "- Item"
  // Avoid splitting repeated markers like "**" (bold), "***" (bold italic), or "---" (horizontal rule)
  processed = processed.replace(/^([*])(?=[^\s*])/gm, '$1 ');
  processed = processed.replace(/^([-+])(?=[^\s-+])/gm, '$1 ');

  // 6. Ensure blank line before bullet / numbered lists when preceded by text
  processed = processed.replace(/([^\n])\n([-*+] )/g, '$1\n\n$2');
  processed = processed.replace(/([^\n])\n(\d+\. )/g, '$1\n\n$2');

  // 7. Clean up excessive blank lines (4+ → 2)
  processed = processed.replace(/\n{4,}/g, '\n\n\n');

  return processed;
}
